import { useContext } from 'react';
import Vec2 from 'vec2';

import { CANVAS_OBJECT_TYPE_PREFIX, FAUX_SHAPE_INDEX, SHAPE_ROTATION_DEGREES } from '@common/constants';
import { ShapeType } from '@common/enums';
import { MathFunctions } from '@common/helpers';
import {
    AppliancesAllowedOnShapes,
    AppliancesAllowedOutsideShapes,
    DrawingAppliance,
    DrawingShape,
} from '@common/models';
import { DrawingShapeContext } from '@ee-context';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import {
    selectActiveDrawingAppliance,
    setNeedRedrawCanvas,
    setPreventRepricing,
    toggleApplianceTrigger,
} from '@redux/slices/drawingShapeSlice';
import { resetSelectedToolbarButton } from '@redux/slices/toolbarCanvasSlice';

import drawingTool from '../design-countertop/drawer/drawingTool';

const useAppliance = () => {
    const dispatch = useAppDispatch();
    const { drawingShapes, updateDrawingShapes } = useContext(DrawingShapeContext);

    const activeDrawingAppliance = useAppSelector(selectActiveDrawingAppliance);

    const transferApplianceBetweenShapes = (
        xferringAppliance: DrawingAppliance,
        sourceShape: DrawingShape,
        targetShape: DrawingShape
    ): void => {
        // delete the appliance from the source shape
        const foundIndex = sourceShape.appliances.findIndex(
            (appliance: DrawingAppliance) =>
                `${CANVAS_OBJECT_TYPE_PREFIX.APPLIANCE}_${appliance.id}` ===
                `${CANVAS_OBJECT_TYPE_PREFIX.APPLIANCE}_${xferringAppliance.id}`
        );
        if (foundIndex > -1) {
            sourceShape.appliances.splice(foundIndex, 1);
        }
        // add the appliance to the target shape
        targetShape.appliances.push(xferringAppliance);
    };

    const updateApplianceByTranslation = (
        moveVec: Vec2,
        movingAppliance: string,
        drawingShapes: DrawingShape[]
        // eslint-disable-next-line sonarjs/cognitive-complexity
    ): DrawingShape | void => {
        let functionComplete = false;
        drawingShapes.forEach((shape: DrawingShape) => {
            const foundIndex = shape.appliances.findIndex(
                (appliance: DrawingAppliance) =>
                    `${CANVAS_OBJECT_TYPE_PREFIX.APPLIANCE}_${appliance.id}` === movingAppliance
            );
            if (foundIndex > -1 && !functionComplete) {
                const oldPosition = shape.appliances[foundIndex].centerPoint;
                const potentialCenterPoint = MathFunctions.subtractVectors(oldPosition, moveVec);
                if (
                    // Just moving an appliance within a shape
                    drawingTool.isApplianceOnCountertop(
                        potentialCenterPoint,
                        shape.appliances[foundIndex],
                        shape,
                        false
                    )
                ) {
                    shape.appliances[foundIndex].centerPoint = potentialCenterPoint;
                    functionComplete = true;
                } else {
                    // see if the appliance was moved to another shape
                    // consider if appliance is allowed on a shape or not on a shape rules
                    let applianceAccepted = false;
                    drawingShapes
                        .filter((otherShape: DrawingShape) => otherShape.type !== ShapeType.FAUX_SHAPE)
                        .forEach((otherShape) => {
                            if (
                                drawingTool.isApplianceOnCountertop(
                                    potentialCenterPoint,
                                    shape.appliances[foundIndex],
                                    otherShape,
                                    false
                                ) &&
                                AppliancesAllowedOnShapes.indexOf(
                                    shape.appliances[foundIndex].applianceModel.applianceType
                                ) > -1
                            ) {
                                applianceAccepted = true;
                                shape.appliances[foundIndex].centerPoint = potentialCenterPoint;
                                transferApplianceBetweenShapes(shape.appliances[foundIndex], shape, otherShape);
                                functionComplete = true;
                            }
                        });
                    if (
                        !applianceAccepted &&
                        AppliancesAllowedOutsideShapes.indexOf(
                            shape.appliances[foundIndex].applianceModel.applianceType
                        ) > -1
                    ) {
                        applianceAccepted = true;
                        shape.appliances[foundIndex].centerPoint = potentialCenterPoint;
                        transferApplianceBetweenShapes(
                            shape.appliances[foundIndex],
                            shape,
                            drawingShapes[FAUX_SHAPE_INDEX]
                        );
                        functionComplete = true;
                    }
                }
            }
        });
    };

    const deleteAllAppliancesByType = (applianceTypes: string[]): void => {
        drawingShapes.forEach((shape: DrawingShape) => {
            for (let applIndex = shape.appliances.length - 1; applIndex >= 0; applIndex--) {
                if (applianceTypes.indexOf(shape.appliances[applIndex].applianceModel.applianceType) > -1) {
                    shape.appliances.splice(applIndex, 1);
                }
            }
        });
        updateDrawingShapes([...drawingShapes]);
        dispatch(setNeedRedrawCanvas(true));
    };

    const applianceTypesInDrawingArea = (applianceTypes: string[]): boolean => {
        return drawingShapes.some((shape: DrawingShape) => {
            return shape.appliances.some(
                (appliance) => applianceTypes.indexOf(appliance.applianceModel.applianceType) > -1
            );
        });
    };

    const getApproximateApplianceOutline = (appliance: DrawingAppliance): Vec2[] => {
        return [
            new Vec2(appliance.centerPoint.x + 22, appliance.centerPoint.y + 22),
            new Vec2(appliance.centerPoint.x + 22, appliance.centerPoint.y - 22),
            new Vec2(appliance.centerPoint.x - 22, appliance.centerPoint.y - 22),
            new Vec2(appliance.centerPoint.x - 22, appliance.centerPoint.y + 22),
            new Vec2(appliance.centerPoint.x + 22, appliance.centerPoint.y + 22),
        ];
    };

    const rotateActiveDrawingAppliance = (canvas: fabric.Canvas, clockwise: boolean) => {
        // find shape for selected appliance
        drawingShapes.forEach((shape: DrawingShape) => {
            const foundIndex = shape.appliances.findIndex(
                (appliance: DrawingAppliance) =>
                    `${CANVAS_OBJECT_TYPE_PREFIX.APPLIANCE}_${appliance.id}` === activeDrawingAppliance
            );
            if (foundIndex > -1) {
                // rotate selected appliance
                let rotInc = SHAPE_ROTATION_DEGREES;
                if (clockwise) {
                    rotInc *= -1;
                }
                dispatch(setPreventRepricing(true));
                shape.appliances[foundIndex].angle -= rotInc;

                const activeCanvasObject = canvas.getActiveObject();
                activeCanvasObject?.rotate(shape.appliances[foundIndex].angle);
                dispatch(toggleApplianceTrigger());

                // reset buttons and drawing state
                dispatch(resetSelectedToolbarButton());
            }
        });
    };

    return {
        updateApplianceByTranslation,
        rotateActiveDrawingAppliance,
        deleteAllAppliancesByType,
        applianceTypesInDrawingArea,
        getApproximateApplianceOutline,
    };
};

export default useAppliance;
